import React from "react";
import { Button, type ButtonProps } from "@progress/kendo-react-buttons";
import type { PermissionCheckResult } from "@presentation/components/helpers/usePermissionCheck";
import { CONTEXT_MANAGEMENT_ACTION_LOCKED_MESSAGE, CONTEXT_MANAGEMENT_ENTITLEMENT_NAME } from "@domain/valueObjects/contextManagementEntitlement";

export interface GatedActionButtonProps extends ButtonProps {
  permission: PermissionCheckResult;
  entitlementActive?: boolean | null;
}

const ENTITLEMENT_CHECKING_TITLE = `Checking ${CONTEXT_MANAGEMENT_ENTITLEMENT_NAME} availability.`;

/**
 * Wraps a management-surface action button with the WP8 rendering contract: an ordinary
 * permission denial still hides the control entirely (role/action matrix - design.md), but an
 * entitlement denial renders it disabled with upgrade messaging instead, since the role itself
 * would otherwise grant it.
 */
export const GatedActionButton: React.FC<GatedActionButtonProps> = ({ permission, entitlementActive, title, disabled, ...buttonProps }) => {
  if (!permission.allowed && permission.reason !== "entitlement") return null;

  const isEntitlementLocked = permission.reason === "entitlement" || (permission.allowed && entitlementActive === false);
  const isCheckingEntitlement = permission.allowed && entitlementActive === null;
  let tooltip = title;
  if (isEntitlementLocked) tooltip = CONTEXT_MANAGEMENT_ACTION_LOCKED_MESSAGE;
  else if (isCheckingEntitlement) tooltip = ENTITLEMENT_CHECKING_TITLE;

  const button = <Button {...buttonProps} disabled={isEntitlementLocked || isCheckingEntitlement || disabled} title={tooltip} />;
  if (isEntitlementLocked || isCheckingEntitlement) {
    return (
      <span title={tooltip} style={{ display: "inline-flex" }}>
        {button}
      </span>
    );
  }
  return button;
};
